// Largely copied from github.com/bolstycjw/chartjs-adapter-dayjs-4/blob/master/src/index.ts

import type { TimeUnit } from "chart.js"
import { _adapters } from "chart.js"

import dayjs, { type ConfigType, type QUnitType } from "dayjs"

// Needed to handle the custom parsing
import CustomParseFormat from "dayjs/plugin/customParseFormat"

// Needed to handle quarter format
import AdvancedFormat from "dayjs/plugin/advancedFormat"

// Needed to handle adding/subtracting quarter
import QuarterOfYear from "dayjs/plugin/quarterOfYear"

// Needed to handle localization format
import LocalizedFormat from "dayjs/plugin/localizedFormat"

import isoWeek from "dayjs/plugin/isoWeek"
import utc from "dayjs/plugin/utc"
import weekOfYear from "dayjs/plugin/weekOfYear"
import timezone from "dayjs/plugin/timezone"
import isSameOrAfter from "dayjs/plugin/isSameOrAfter"
import relativeTime from "dayjs/plugin/relativeTime"

dayjs.extend(AdvancedFormat)
dayjs.extend(QuarterOfYear)
dayjs.extend(LocalizedFormat)
dayjs.extend(CustomParseFormat)
dayjs.extend(isoWeek)
dayjs.extend(utc)
dayjs.extend(weekOfYear)
dayjs.extend(timezone)
dayjs.extend(isSameOrAfter)
dayjs.extend(relativeTime)

const FORMATS = {
  datetime: "MMM D, YYYY, h:mm:ss a",
  millisecond: "h:mm:ss.SSS a",
  second: "h:mm:ss a",
  minute: "h:mm a",
  hour: "hA",
  day: "MMM D",
  week: "ll",
  month: "MMM YYYY",
  quarter: "[Q]Q - YYYY",
  year: "YYYY",
}

export const useInstallDayJsChartJsAdapter = () => {
  const { locale } = useI18n()

  _adapters._date.override({
    //_id: 'dayjs', //DEBUG,
    formats: () => FORMATS,
    parse: function (value: unknown, format?: TimeUnit) {
      if (value === null || typeof value === "undefined") {
        return null
      }

      if (typeof value === "string" && typeof format === "string") {
        return dayjs(value, format).isValid() ? dayjs(value, format).valueOf() : null
      } else if (!(value instanceof dayjs)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return dayjs(value as any).isValid() ? dayjs(value as any).valueOf() : null
      }
      return null
    },
    format: function (time: ConfigType, format: TimeUnit): string {
      // Custom formatter to use the correct locale
      // Does not work reactively inherently, the chart labels need to be in a computed property
      return dayjs(time).local().locale(locale.value).format(format)
    },
    add: function (time: ConfigType, amount: number, unit: QUnitType & TimeUnit) {
      return dayjs(time).add(amount, unit).valueOf()
    },
    diff: function (max: ConfigType, min: ConfigType, unit: TimeUnit) {
      return dayjs(max).diff(dayjs(min), unit)
    },
    startOf: function (time: ConfigType, unit: (TimeUnit & QUnitType) | "isoWeek", weekday?: number) {
      if (unit === "isoWeek") {
        // Ensure that weekday has a valid format
        //const formattedWeekday

        const validatedWeekday: number = typeof weekday === "number" && weekday > 0 && weekday < 7 ? weekday : 1

        return dayjs(time).isoWeekday(validatedWeekday).startOf("day").valueOf()
      }

      return dayjs(time).startOf(unit).valueOf()
    },
    endOf: function (time: ConfigType, unit: TimeUnit & QUnitType) {
      return dayjs(time).endOf(unit).valueOf()
    },
  })
}
